import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import {
  Dumbbell, Clock, ChevronRight, Lock, AlertCircle, Play, Pause, RotateCcw, Timer, Volume2, VolumeX, Flame, Users, CalendarCheck, Check, Star
} from 'lucide-react';
import { toast } from 'sonner';

const GROUP_COLORS = {
  Dos: '#3b82f6',
  Pectoraux: '#ef4444',
  Jambes: '#10b981',
  'Épaules': '#f59e0b',
  Bras: '#FF6B35'
};

// HIIT Timer Component
function HiitTimer({ workSeconds = 60, restSeconds = 60, rounds = 3, onClose }) {
  const [phase, setPhase] = useState('idle'); // idle | work | rest | done
  const [currentRound, setCurrentRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(workSeconds);
  const [soundOn, setSoundOn] = useState(true);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Refs (audioCtxRef, intervalRef) are stable and don't need to be in dependency arrays
  // Updater function params (prev, r) are not external dependencies
  const playBeep = useCallback((freq = 800, duration = 200) => {
    if (!soundOn) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + duration / 1000);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Web Audio:', err);
    }
  }, [soundOn]);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopTimer();
  }, [stopTimer]);

  useEffect(() => {
    if (phase === 'idle' || phase === 'done') {
      stopTimer();
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Phase transition
          if (phase === 'work') {
            playBeep(400, 500); // Low beep = rest
            setPhase('rest');
            return restSeconds;
          } else if (phase === 'rest') {
            if (currentRound >= rounds) {
              playBeep(1000, 800); // High beep = done
              setPhase('done');
              return 0;
            }
            playBeep(800, 300); // Medium beep = next round
            setCurrentRound(r => r + 1);
            setPhase('work');
            return workSeconds;
          }
        }
        if (prev <= 4 && prev > 1) playBeep(600, 100); // Countdown beeps
        return prev - 1;
      });
    }, 1000);

    return () => stopTimer();
  }, [phase, currentRound, rounds, workSeconds, restSeconds, playBeep, stopTimer]);

  const startTimer = () => {
    setCurrentRound(1);
    setTimeLeft(workSeconds);
    setPhase('work');
    playBeep(800, 300);
  };

  const resetTimer = () => {
    stopTimer();
    setPhase('idle');
    setCurrentRound(1);
    setTimeLeft(workSeconds);
  };

  const togglePause = () => {
    if (phase === 'work' || phase === 'rest') {
      stopTimer();
      setPhase('idle');
    }
  };

  const formatTime = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercent = phase === 'work'
    ? ((workSeconds - timeLeft) / workSeconds) * 100
    : phase === 'rest'
    ? ((restSeconds - timeLeft) / restSeconds) * 100
    : 0;

  return (
    <div className="fixed inset-0 z-50 bg-[#0F0F0F]/95 backdrop-blur-md flex flex-col items-center justify-center p-6" data-testid="hiit-timer">
      {/* Close */}
      <button onClick={() => { stopTimer(); onClose(); }} className="absolute top-4 right-4 text-zinc-500 hover:text-white p-2">
        <RotateCcw className="w-6 h-6" />
      </button>

      {/* Sound Toggle */}
      <button onClick={() => setSoundOn(!soundOn)} className="absolute top-4 left-4 text-zinc-500 hover:text-white p-2">
        {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>

      {/* Round */}
      <p className="text-zinc-500 text-sm mb-2">
        Round {currentRound}/{rounds}
      </p>

      {/* Phase Label */}
      {(() => {
        const PHASE_COLORS = { work: 'text-red-400', rest: 'text-green-400', done: 'text-[#FF6B35]' };
        const PHASE_LABELS = { work: 'TRAVAIL', rest: 'REPOS', done: 'TERMINÉ !' };
        const STROKE_COLORS = { work: '#ef4444', rest: '#10b981' };
        return (
          <>
            <p className={`text-2xl font-bold mb-6 ${PHASE_COLORS[phase] || 'text-zinc-400'}`}>
              {PHASE_LABELS[phase] || 'PRÊT ?'}
            </p>

            {/* Timer Circle */}
            <div className="relative w-56 h-56 mb-8">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke="#27272a" strokeWidth="4" />
                <circle
                  cx="50" cy="50" r="45" fill="none"
                  stroke={STROKE_COLORS[phase] || '#FF6B35'}
                  strokeWidth="4"
                  strokeDasharray={`${2 * Math.PI * 45}`}
                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - progressPercent / 100)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-mono text-5xl font-bold text-white">{formatTime(timeLeft)}</span>
              </div>
            </div>
          </>
        );
      })()}

      {/* Controls */}
      <div className="flex gap-4">
        {phase === 'idle' && !((phase === 'idle') && currentRound > 1 && timeLeft === workSeconds && currentRound === 1) && (
          <Button onClick={startTimer} className="bg-[#FF6B35] text-black hover:bg-[#E85A25] px-8 py-6 text-lg" data-testid="start-timer">
            <Play className="w-6 h-6 mr-2" /> GO !
          </Button>
        )}
        {(phase === 'work' || phase === 'rest') && (
          <>
            <Button onClick={togglePause} variant="outline" className="border-zinc-600 text-zinc-300 px-6 py-6">
              <Pause className="w-6 h-6 mr-2" /> Pause
            </Button>
            <Button onClick={resetTimer} variant="outline" className="border-zinc-600 text-zinc-300 px-6 py-6">
              <RotateCcw className="w-6 h-6 mr-2" /> Reset
            </Button>
          </>
        )}
        {phase === 'done' && (
          <Button onClick={() => { resetTimer(); onClose(); }} className="bg-[#FF6B35] text-black hover:bg-[#E85A25] px-8 py-6 text-lg">
            Fermer
          </Button>
        )}
      </div>

      {/* Config display */}
      <p className="text-xs text-zinc-500 mt-6">
        {workSeconds}s travail / {restSeconds}s repos / {rounds} rounds
      </p>
    </div>
  );
}


export default function MonProgrammePage({ embedded = false }) {
  const { user } = useAuth();
  const [myProgram, setMyProgram] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewAll, setViewAll] = useState(false);
  const [timerConfig, setTimerConfig] = useState(null); // {work, rest, rounds}

  const fetchData = useCallback(async () => {
    try {
      const [myRes, allRes] = await Promise.all([
        apiClient.get('/my-program'),
        apiClient.get('/fixed-programs')
      ]);
      setMyProgram(myRes.data);
      setAllPrograms(allRes.data);
    } catch {
      toast.error('Erreur chargement programme');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const launchTimer = (exercise) => {
    // Parse timer config from exercise
    const sets = parseInt(exercise.sets) || 3;
    const restMatch = exercise.rest?.match(/(\d+)/);
    const restSec = restMatch ? parseInt(restMatch[1]) : 60;
    setTimerConfig({ work: 60, rest: restSec, rounds: sets });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const programsToShow = viewAll ? allPrograms : (myProgram?.weekly_plan || (myProgram?.assigned ? [myProgram] : []));

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-[#0F0F0F] ${embedded ? 'pb-4' : 'pb-20'}`}>
      {/* HIIT Timer Overlay */}
      {timerConfig && (
        <HiitTimer
          workSeconds={timerConfig.work}
          restSeconds={timerConfig.rest}
          rounds={timerConfig.rounds}
          onClose={() => setTimerConfig(null)}
        />
      )}

      {!embedded && (
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-white">Programme</h1>
          <p className="text-xs text-zinc-500">Tes exercices de la semaine</p>
        </div>
      </header>
      )}

      <main className="p-4 space-y-4 animate-fade-in">
        {/* Toggle */}
        <div className="flex gap-2" data-testid="program-toggle">
          <button
            onClick={() => setViewAll(false)}
            className={`flex-1 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              !viewAll ? 'bg-[#FF6B35] text-black' : 'bg-[#1B1B1B] text-zinc-400 border border-[#2A2A2E]'
            }`}
          >
            Mon programme
          </button>
          <button
            onClick={() => setViewAll(true)}
            className={`flex-1 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              viewAll ? 'bg-[#FF6B35] text-black' : 'bg-[#1B1B1B] text-zinc-400 border border-[#2A2A2E]'
            }`}
          >
            Tous les programmes
          </button>
        </div>

        {/* My Group Info */}
        {!viewAll && myProgram?.assigned && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="my-group-info">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-sm flex items-center justify-center text-xl font-bold"
                style={{ backgroundColor: `${GROUP_COLORS[myProgram.muscle_group] || '#FF6B35'}20`, color: GROUP_COLORS[myProgram.muscle_group] || '#FF6B35' }}
              >
                {myProgram.group_num}
              </div>
              <div>
                <p className="text-white font-bold">Groupe {myProgram.group_num} — {myProgram.muscle_group}</p>
                <p className="text-xs text-zinc-500">{myProgram.day} &middot; Semaine du {myProgram.week_start}</p>
              </div>
              <Lock className="w-4 h-4 text-zinc-500 ml-auto" />
            </div>
          </div>
        )}

        {/* Not assigned */}
        {!viewAll && !myProgram?.assigned && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-6 text-center" data-testid="not-assigned">
            <AlertCircle className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 mb-1">{myProgram?.message || 'Pas de programme assigné'}</p>
            <p className="text-xs text-zinc-500">Marque ta présence et attends la génération des groupes</p>
          </div>
        )}

        {/* Programs */}
        {programsToShow.map((prog, progIdx) => {
          const isCyclic = !viewAll && myProgram?.weekly_plan;
          const muscle = viewAll ? prog.muscle_group : (isCyclic ? prog.muscle_group : myProgram.muscle_group);
          const day = viewAll ? prog.day : (isCyclic ? prog.day : myProgram.day);
          const groupNum = viewAll ? prog.group_num : (isCyclic ? prog.group_num : myProgram.group_num);
          const sections = prog.sections || [];
          const color = GROUP_COLORS[muscle] || '#FF6B35';
          const keyId = isCyclic ? `day-${progIdx}` : groupNum;

          return (
            <div key={keyId} className="space-y-3" data-testid={`program-${keyId}`}>
              {/* Program Header — show day + muscle for cyclic view */}
              {(viewAll || isCyclic) && (
                <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" style={{ borderTopColor: color, borderTopWidth: '3px' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center text-lg font-bold" style={{ backgroundColor: `${color}20`, color }}>
                      {isCyclic ? day?.substring(0,3) : groupNum}
                    </div>
                    <div>
                      <p className="text-white font-bold">{muscle}</p>
                      <p className="text-xs text-zinc-400">{day} &middot; {prog.duration || ''} &middot; {prog.total_sets || ''}</p>
                    </div>
                  </div>
                  {prog.objective && <p className="text-xs text-zinc-400 mt-2">{prog.objective}</p>}
                </div>
              )}

              {/* Vidéo du programme (toute la séance) */}
              {prog.program_video && (
                <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3" style={{ borderLeftColor: color, borderLeftWidth: '3px' }}>
                  <p className="text-[10px] font-bold mb-2" style={{ color }}>VIDÉO DE LA SÉANCE</p>
                  <video src={prog.program_video} controls preload="metadata" playsInline
                    className="w-full max-h-60 rounded-sm border border-zinc-700 bg-black object-contain"
                    data-testid={`program-video-${keyId}`} />
                </div>
              )}

              {/* Sections (EXO 1, 2, 3...) */}
              {sections.map((section) => {
                const isFinisher = section.color === 'red';
                const isWarmup = section.color === 'yellow';
                const sectionColor = isFinisher ? '#ef4444' : isWarmup ? '#f59e0b' : color;

                return (
                  <div key={`${groupNum}-s${section.exo_num}`} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden">
                    {/* Section Header */}
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#2A2A2E]" style={{ borderLeftColor: sectionColor, borderLeftWidth: '3px' }}>
                      {section.exo_num > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${sectionColor}20`, color: sectionColor }}>
                          EXO {section.exo_num}
                        </span>
                      )}
                      {section.exo_num === 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-yellow-500/20 text-yellow-400">WARM-UP</span>
                      )}
                      <span className="text-sm font-medium text-white">{section.title}</span>
                    </div>

                    {/* Vidéo de démonstration du bloc */}
                    {section.video && (
                      <div className="px-4 pt-3">
                        <video src={section.video} controls preload="metadata" playsInline
                          className="w-full max-h-52 rounded-sm border border-zinc-700 bg-black object-contain"
                          data-testid={`section-video-${section.exo_num}`} />
                      </div>
                    )}

                    {/* Exercises in section */}
                    <div className="divide-y divide-zinc-800/30">
                      {section.exercises.map((ex, eIdx) => (
                        <div key={`${groupNum}-${section.exo_num}-${eIdx}`} className={`p-3 ${ex.is_finisher ? 'bg-red-500/5' : ''}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5 ${
                              ex.is_finisher ? 'bg-red-500/20 text-red-400' : ex.superset ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-800 text-zinc-500'
                            }`}>
                              {eIdx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${ex.is_finisher ? 'text-red-400' : 'text-white'}`}>{ex.name}</p>
                              {ex.superset && <span className="text-[9px] text-purple-400 font-bold">SUPERSET</span>}
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                <span className="text-[11px] text-zinc-400 flex items-center gap-1">
                                  <Dumbbell className="w-3 h-3" /> {ex.sets}x{ex.reps}
                                </span>
                                {ex.weight && ex.weight !== '-' && <span className="text-[11px] text-zinc-500">{ex.weight}</span>}
                                <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> {ex.rest}
                                </span>
                                {ex.tempo && ex.tempo !== '-' && <span className="text-[11px] text-zinc-500 italic">{ex.tempo}</span>}
                              </div>
                              {ex.target && <p className="text-[10px] text-[#FF6B35]/70 mt-1">Cible: {ex.target}</p>}
                              {/* Exercise Media (photos/videos) */}
                              {(ex.media || []).length > 0 && (
                                <div className="flex gap-2 mt-2 overflow-x-auto scrollbar-none">
                                  {ex.media.map((url, mi) => {
                                    const isUploadedVideo = url.includes('/api/videos/') || /\.(mp4|webm|mov)(\?|$)/i.test(url);
                                    if (isUploadedVideo) {
                                      return (
                                        <video key={`${url}-${mi}`} src={url} controls preload="metadata" playsInline
                                          className="flex-shrink-0 w-44 h-28 rounded-sm border border-zinc-700 bg-black object-contain"
                                          data-testid="exercise-video" />
                                      );
                                    }
                                    const isVideo = url.includes('youtube') || url.includes('youtu.be');
                                    if (isVideo) {
                                      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
                                      return ytMatch ? (
                                        <a key={`${url}-${mi}`} href={url} target="_blank" rel="noreferrer" className="flex-shrink-0 w-28 h-20 rounded-sm overflow-hidden border border-zinc-700 relative block">
                                          <img src={`https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center"><div className="w-0 h-0 border-l-[8px] border-l-white border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent ml-0.5" /></div>
                                          </div>
                                        </a>
                                      ) : (
                                        <a key={`${url}-${mi}`} href={url} target="_blank" rel="noreferrer" className="flex-shrink-0 w-20 h-20 rounded-sm bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                                          <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                                        </a>
                                      );
                                    }
                                    return (
                                      <a key={`${url}-${mi}`} href={url} target="_blank" rel="noreferrer" className="flex-shrink-0">
                                        <img src={url} alt="" className="w-20 h-20 rounded-sm object-cover border border-zinc-700" />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Timer for finishers */}
                              {ex.is_finisher && (
                                <button
                                  onClick={() => launchTimer(ex)}
                                  className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-sm text-red-400 text-xs font-medium hover:bg-red-500/30 transition-colors"
                                  data-testid={`timer-btn-${groupNum}-${section.exo_num}`}
                                >
                                  <Timer className="w-3.5 h-3.5" /> Lancer le timer HIIT
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Analysis */}
              {viewAll && prog.analysis && (
                <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4 space-y-2">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-sm p-3">
                    <p className="text-[10px] text-green-400 font-bold mb-1">Points forts</p>
                    {prog.analysis.points_forts?.map((p, i) => <p key={`fort-${p}`} className="text-[11px] text-green-300/80">+ {p}</p>)}
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-3">
                    <p className="text-[10px] text-yellow-400 font-bold mb-1">Vigilance</p>
                    {prog.analysis.vigilance?.map((v, i) => <p key={`vig-${v}`} className="text-[11px] text-yellow-300/80">- {v}</p>)}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* My Active Challenges */}
        {!viewAll && (myProgram?.my_challenges || []).length > 0 && (
          <div className="space-y-3" data-testid="my-challenges">
            <div className="flex items-center gap-2 px-1">
              <Flame className="w-4 h-4 text-[#FF6B35]" />
              <h3 className="text-sm font-medium text-zinc-400">Mes défis en cours</h3>
            </div>
            {myProgram.my_challenges.map((ch) => {
              const daysLeft = ch.event_date
                ? Math.ceil((new Date(ch.event_date) - new Date()) / (1000*60*60*24))
                : ch.deadline
                ? Math.ceil((new Date(ch.deadline) - new Date()) / (1000*60*60*24))
                : null;
              return (
                <div key={ch.id} className="bg-[#1B1B1B] border border-[#2A2A2E] border-l-4 border-l-[#FF6B35] rounded-sm p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-white font-bold text-sm">{ch.title}</h4>
                      <p className="text-xs text-zinc-500 mt-0.5">{ch.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ch.event_date && (
                        <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-sm flex items-center gap-1">
                          <CalendarCheck className="w-3 h-3" />
                          {new Date(ch.event_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {daysLeft != null && daysLeft > 0 && (
                        <span className="text-[10px] bg-[#FF6B35]/20 text-[#FF6B35] px-2 py-0.5 rounded-sm">
                          J-{daysLeft}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Rewards */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5">
                      <Star className="w-2.5 h-2.5" /> +{ch.reward_points} pts
                    </span>
                    {ch.reward_badge && (
                      <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-sm">{ch.reward_badge}</span>
                    )}
                  </div>

                  {/* Participants */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <Users className="w-3 h-3 text-zinc-500 mr-1" />
                    {ch.participants.map((p) => (
                      <span
                        key={p.name}
                        className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                          p.completed ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-400'
                        }`}
                      >
                        {p.completed && <Check className="w-2.5 h-2.5 inline mr-0.5" />}{p.name}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      {!embedded && <BottomNav />}
    </div>
  );
}
